import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { AppButton, AppIconButton, AppTextField } from "../../../components/form";
import { AppActionsRow, PageSectionCard } from "../../../components/layout";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { platformAdminService, type PlatformUser } from "../../../services/platformAdminService";

export default function UsersPanel() {
  const { t } = useTranslation();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const navigate = useNavigate();
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<PlatformUser | null>(null);

  const load = useCallback(async (q?: string) => {
    setLoading(true);
    setError(null);
    try {
      const rows = await platformAdminService.listUsers(q, 0, 200);
      setUsers(Array.isArray(rows) ? rows : []);
    } catch (e: any) {
      setError(e?.message || t("superAdmin.users.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleEnabled = async (user: PlatformUser) => {
    setBusyId(user.userId);
    setError(null);
    setMessage(null);
    try {
      const updated = await platformAdminService.setUserEnabled(user.userId, !user.enabled);
      setUsers((prev) => prev.map((u) => (u.userId === updated.userId ? { ...u, ...updated } : u)));
      setMessage(
        updated.enabled
          ? t("superAdmin.users.enabled", { user: updated.username })
          : t("superAdmin.users.disabled", { user: updated.username })
      );
    } catch (e: any) {
      setError(e?.message || t("superAdmin.users.toggleError"));
    } finally {
      setBusyId(null);
    }
  };

  const deleteUser = async (user: PlatformUser) => {
    try {
      await confirmDelete({
        itemName: user.username || user.email || user.userId,
        titleKey: "superAdmin.users.deleteConfirmTitle",
        descriptionKey: "superAdmin.users.deleteConfirmDescription",
      });
    } catch (e) {
      if (isCancelled(e)) return;
      throw e;
    }
    setBusyId(user.userId);
    setError(null);
    setMessage(null);
    try {
      await platformAdminService.deleteUser(user.userId);
      setUsers((prev) => prev.filter((u) => u.userId !== user.userId));
      setMessage(t("superAdmin.users.deleted", { user: user.username }));
    } catch (e: any) {
      setError(e?.message || t("superAdmin.users.deleteError"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}
      <PageSectionCard
        title={t("superAdmin.users.title")}
        description={t("superAdmin.users.description")}
      >
        <AppActionsRow align="start">
          <AppTextField
            size="small"
            label={t("superAdmin.users.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void load(search);
            }}
            sx={{ minWidth: 260 }}
          />
          <AppButton variant="outlined" onClick={() => void load(search)} disabled={loading}>
            {t("common.search")}
          </AppButton>
        </AppActionsRow>
        {loading ? (
          <CircularProgress size={28} />
        ) : users.length === 0 ? (
          <Alert severity="info">{t("superAdmin.users.empty")}</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("superAdmin.users.username")}</TableCell>
                <TableCell>{t("superAdmin.users.email")}</TableCell>
                <TableCell>{t("superAdmin.users.workspace")}</TableCell>
                <TableCell>{t("superAdmin.users.status")}</TableCell>
                <TableCell align="right">{t("superAdmin.users.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.userId} hover>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>
                    {user.email || "—"}
                    {user.provisionedByIntegration && (
                      <Chip size="small" color="info" variant="outlined" sx={{ ml: 1 }} label={t("superAdmin.users.integrationChip")} />
                    )}
                  </TableCell>
                  <TableCell>{user.workspaceName || user.tenantId || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        user.enabled
                          ? t("superAdmin.users.active")
                          : t("superAdmin.users.inactive")
                      }
                      color={user.enabled ? "success" : "warning"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <AppIconButton
                      size="small"
                      title={t("common.actions")}
                      disabled={busyId === user.userId}
                      onClick={(e) => {
                        setMenuAnchor(e.currentTarget);
                        setMenuUser(user);
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </AppIconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PageSectionCard>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor && menuUser)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuUser(null);
        }}
      >
        <MenuItem
          disabled={!menuUser?.tenantId}
          onClick={() => {
            if (!menuUser?.tenantId) return;
            const workspaceId = menuUser.tenantId;
            setMenuAnchor(null);
            setMenuUser(null);
            navigate(`/super-admin/customers?workspace=${encodeURIComponent(workspaceId)}`);
          }}
        >
          <ListItemIcon>
            <StorefrontOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("superAdmin.users.openCustomer")}</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={busyId === menuUser?.userId}
          onClick={() => {
            if (!menuUser) return;
            const user = menuUser;
            setMenuAnchor(null);
            setMenuUser(null);
            void toggleEnabled(user);
          }}
        >
          <ListItemIcon>
            {menuUser?.enabled ? (
              <BlockOutlinedIcon fontSize="small" color="warning" />
            ) : (
              <CheckCircleOutlinedIcon fontSize="small" color="success" />
            )}
          </ListItemIcon>
          <ListItemText>
            {menuUser?.enabled
              ? t("superAdmin.users.disable")
              : t("superAdmin.users.enable")}
          </ListItemText>
        </MenuItem>
        <MenuItem
          disabled={busyId === menuUser?.userId}
          onClick={() => {
            if (!menuUser) return;
            const user = menuUser;
            setMenuAnchor(null);
            setMenuUser(null);
            void deleteUser(user);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t("superAdmin.users.delete")}</ListItemText>
        </MenuItem>
      </Menu>
    </Stack>
  );
}
